import React from 'react'

import { IUI2FormContext, UI2FormContext } from './ui2-form-context'

/**
 * Injects UI2FormContext values as props on a UI2 field/section.
 *
 * Precedence rules:
 *  - `formDensity`, `isRequired`, `isOptional` — child prop wins over context
 *    (context supplies a form-level default the child can override).
 *  - `isReadOnly`, `isDisabled` — context wins when the form sets it to
 *    `true`. A form-enforced read-only/disabled state cannot be overridden
 *    by a child prop. When the form leaves either undefined (or sets it to
 *    `false`), the child prop takes effect.
 *
 * Forwards refs so the entity renderer can track the wrapped class instance
 * for UI-driven validation.
 */
export function withUI2FormContext<P extends Partial<IUI2FormContext>>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  WrappedComponent: any
): React.ComponentType<P> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component'

  const WithUI2FormContext = React.forwardRef<unknown, P>((props, ref) => (
    <UI2FormContext.Consumer>
      {(context) => {
        const merged: IUI2FormContext & Record<string, unknown> = {
          ...context,
          ...props,
        }
        // Force-on: context wins whenever the form set it to true.
        if (context.isReadOnly === true) {
          merged.isReadOnly = true
        }
        if (context.isDisabled === true) {
          merged.isDisabled = true
        }
        // Force-null: completed stacked sections suppress field-level decorations.
        if (context.inlineHelpText === null) {
          merged.inlineHelpText = null
          merged.isRequired = false
          merged.isOptional = false
        }
        return <WrappedComponent {...merged} ref={ref} />
      }}
    </UI2FormContext.Consumer>
  ))

  WithUI2FormContext.displayName = `WithUI2FormContext(${displayName})`
  // Forward renderer-read statics so the entity renderer's scope-isolation
  // detection still works when a component is wrapped by this HOC.
  if (WrappedComponent.isolatesChildValueScope !== undefined) {
    ;(WithUI2FormContext as any).isolatesChildValueScope = WrappedComponent.isolatesChildValueScope
  }
  return WithUI2FormContext as unknown as React.ComponentType<P>
}
